/**
 * @file 函数解析器相关方法
 */
import {DYNAMIC_PARAMETER, FUNCTION_TYPE, PARAMETER_TYPE, PARAMETER_VALUE_TYPE} from './constant';
import { concatExpressions, splitByLogic } from './functionImpl/expressionFunctionImpl';
import functionImpl from './functionImpl';
import { Result } from './type';
import { setAttrImpl, setExpression } from './functionImpl/componentStoreFunctionImpl';
import {getRenderComponentById} from "../../store/common/action";

/**
 * 自定义函数解析方法
 * @param functionBody
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const parseFunction = async (functionBody: any, customParamsList: any, customFunctionParams: any, instanceId: any) => {
  let result
  let copy_functionBody= _cloneDeep(functionBody)
  for (let func of copy_functionBody) {
    //执行判断条件
    let bool = await parseCondition(func, customParamsList, customFunctionParams, instanceId);
    if (bool && _get(func, 'body.length') > 0) {
      let { body } = func;
      let assignmentIndex = body.findIndex(item => {
        return item.paramType === PARAMETER_TYPE.ASSIGNMENT;
      });
      if (assignmentIndex === -1) {
        // 普通函数
        result = await parseBody(body, customParamsList, customFunctionParams, instanceId);
      } else {
        // 赋值语句
        const {
          targetKey,
          targetObj,
          value,
        } = await parseElseFunction(body, assignmentIndex, customParamsList, customFunctionParams, instanceId);
        const testRegex = /^attrs\..+\.value$/;
        const matchRegex = /^attrs\.(.+)\.value$/;
        if (testRegex.test(targetKey)) {
          //满足'attrs.${attr}.value'形式的目标key视为改变组件属性
          let attr = targetKey.match(matchRegex)[1];
          if (targetObj){
            let componentProp = targetObj.prop || targetObj;
            await setAttrImpl(componentProp, attr, value, instanceId);
          }
        } else {
          //其余情况替换动态参数的值
          _set(targetObj, targetKey, value);
        }
      }
    }
  }
  return result;
};

/**
 * 执行表达式if判断
 * @param params
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const parseConditionIf = async (params, customParamsList, customFunctionParams, instanceId) => {
  let splitList = splitByLogic(params);
  let expression: any = [];
  for (let i = 0; i < splitList.length; i++) {
    const currentItem = splitList[i];
    if (!currentItem.some(item => item.paramType === PARAMETER_TYPE.LOGIC)) {
      // 处理非 logic 类型的数组项
      // console.log('处理项:', currentItem);
      let operatorIndex = currentItem.findIndex(item => {
        return item.paramType === PARAMETER_TYPE.OPERATOR;
      });
      if (operatorIndex === -1) {
        return await parseBody(currentItem, customParamsList, customFunctionParams, instanceId);
      } else {
        const {
          targetKey,
          targetObj,
          value,
        } = await parseElseFunction(currentItem, operatorIndex, customParamsList, customFunctionParams, instanceId);
        let componentProp = targetObj?.prop || targetObj;
        if (componentProp) {
          expression.push(...await setExpression(componentProp, targetKey, currentItem, value, instanceId));
        } else {
          expression.push(...currentItem);
        }
      }
    } else {
      expression.push(...currentItem);
    }
  }
  return expression;
};

export const parseBody = async (body, customParamsList, customFunctionParams, instanceId) => {
  let result = new Result();
  return await result.callFunction(body, customParamsList, customFunctionParams, instanceId);
};

/**
 * 解析操作符
 * @param body 函数体
 * @param operatorIndex 操作符下标
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
const parseElseFunction = async (body, operatorIndex, customParamsList, customFunctionParams, instanceId) => {
  let targetKey = "", targetObj, value = {};
  // 操作符截取：左中右，A.A...A.a = B.B...B.b
  let lefts = _cloneDeep(body.slice(0, operatorIndex));
  // 左边形成链式A.A...A
  const leftChain = _size(lefts) > 1 ? lefts.slice(0, -1) : lefts
  targetObj = await parseFunction([{ body: leftChain }], customParamsList, customFunctionParams, instanceId);
  // key：.a
  const key = lefts.slice(-1)[0]
  // 特殊处理，支持方法和参数类型
  let param
  if (key.inParams && key.inParams.length > 0) {
    param = key.inParams[0];
  } else {
    param = _cloneDeep(key)
  }

  targetKey = await parseBody([param], customParamsList, customFunctionParams, instanceId)
  // 右边链式
  const rightChain = _cloneDeep(body.slice(operatorIndex + 1));
  value = await parseFunction([{ body: rightChain }], customParamsList, customFunctionParams, instanceId);
  // console.log("targetKey, targetObj, value-------------",targetKey, targetObj, value)
  return { targetKey, targetObj, value };
};

/**
 * 标准函数解析方法
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 * @param returnVal
 */
export const parseSingleFunction = async (func: any, customParamsList: any, customFunctionParams: any, instanceId: any, returnVal?: any) => {
  if (returnVal) {
    //获取返回值
    try {
      return await functionImpl[_get(func, 'functionType')](func, customParamsList, customFunctionParams, instanceId);
    } catch (e) {
      console.error(`解析函数${_get(func, 'functionType')}失败`, e);
      return 0;
    }
  } else {
    try {
      await functionImpl[_get(func, 'functionType')](func, customParamsList, customFunctionParams, instanceId);
    } catch (e) {
      console.error(`解析函数${_get(func, 'functionType')}失败`, e);
    }
  }
};

/**
 * 参数解析方法
 * @param param
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 * @param returnVal
 */
export const parseParam = async (param: any, customParamsList: any, customFunctionParams: any, instanceId: any, returnVal: any = true) => {
  // console.log("parseParam-----------", param);
  switch (param.paramType) {
    //固定值
    case PARAMETER_TYPE.FIXED: {
      switch (param.valueType) {
        case PARAMETER_VALUE_TYPE.Number: {
          return parseFloat(param.value);
        }
        case PARAMETER_VALUE_TYPE.Object: {
          return JSON.parse(param.value);
        }
        default: {
          return param.value;
        }
      }
    }
    //运算符
    case PARAMETER_TYPE.LOGIC: {
      return param.value;
    } //运算符
    case PARAMETER_TYPE.OPERATOR: {
      return param.value;
    }
    case PARAMETER_TYPE.DYNAMIC: {
      const structureObj  = customParamsList.find(item => {
        return item.prop === param.prop;
      });
      let dynamicValue = structureObj.value
      if (structureObj?.key === DYNAMIC_PARAMETER.CURRENT_COMPONENT) {
        dynamicValue = getRenderComponentById(instanceId,structureObj?.value?.prop);
      }
      return dynamicValue
    }
    //自定义函数
    case PARAMETER_TYPE.CUSTOM_FUNCTION: {
      // let paramDef = customFunctionParams.find(item => {
      //   return item.prop === param.prop;
      // });
      // console.log("自定义函数数据显示 开始3-----------", _get(paramDef, 'functionBody'));
      return await parseFunction(param.functionBody, customParamsList, customFunctionParams, instanceId);
    }
    //基础函数
    case PARAMETER_TYPE.STANDARD_FUNCTION: {
      if (returnVal) {
        return await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, true);
      } else {
        await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, false);
        break;
      }
    }
    case PARAMETER_TYPE.ELEMENT_FUNCTION: {
      if (returnVal) {
        return await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, true);
      } else {
        await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, false);
        break;
      }
    }
    case PARAMETER_TYPE.ROUTE_FUNCTION: {
      if (returnVal) {
        return await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, true);
      } else {
        await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, false);
        break;
      }
    }
    case PARAMETER_TYPE.MATHS_FUNCTION: {
      if (returnVal) {
        return await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, true);
      } else {
        await parseSingleFunction(param, customParamsList, customFunctionParams, instanceId, false);
        break;
      }
    }
    //括号
    case PARAMETER_TYPE.PRIORITY: {
      return `(${await concatExpressions(_get(param, 'inParams'), customParamsList, customFunctionParams, instanceId)})`;
    }
    case PARAMETER_TYPE.API_INFO: {
      return JSON.parse(_get(param, 'value'));
    }
    case PARAMETER_TYPE.MENU: {
      return _get(param, 'value');
    }
    case PARAMETER_TYPE.DATA_SOURCE: {
      return param.value;
    }
  }
};

/**
 * 判断条件解析方法
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const parseCondition = async (func, customParamsList, customFunctionParams, instanceId) => {
  let bool = true;
  if (_size(_get(func, 'conditions.inParams')) > 0) {
    //执行判断语句
    bool = await functionImpl[FUNCTION_TYPE.EXPRESSION_CALCULATION](_get(func, 'conditions'), customParamsList, customFunctionParams, instanceId);
  }
  return bool;
};

/**
 * 替换动态参数的值
 * @param customParamsList
 * @param key
 * @param value
 */
export const replaceDynamicParameter = (customParamsList, key, value) => {
  // 防止空指正异常
  if (!customParamsList) {
    customParamsList = []
  }
  // 匹配所有符合条件项
  const matchParams = customParamsList.filter(item => {
    return _get(item, 'key') === key || _get(item, 'value') === key;
  });
  matchParams.forEach(param => {
    param.value = value;
  });
};

/**
 * 检查组件的监听事件是否存在循环依赖
 * @param components
 */
export const checkCircleDependency = (components) => {
  // 过滤出除了当前配置了监听事件的所有组件属性
  const attrs = components.flatMap(item => {
    return Object.keys(item.attrs).map(attr => {
      return {
        ...item.attrs[attr],
        attr,
        prop: item.prop,
      };
    });
  }).filter(item => {
    return _get(item, 'triggerSetting.functionBody.length', 0) > 0;
  });
  // 以'组件prop__属性'的形式生成节点
  const nodes = attrs.map(item => {
    return `${item.prop}__${item.attr}`;
  });
  // 生成边
  const edges = generateEdges(attrs);

  //邻接矩阵
  const graph = {};
  //结点个数和边的个数
  //标记矩阵,0为当前结点未访问,1为访问过,-1表示当前结点后边的结点都被访问过。
  const visited = {};
  //是否是DAG（有向无环图）
  let isDAG = true;
  edges.forEach(e => {
    const { source, target } = e;
    if (!nodes.includes(source)) {
      nodes.push(source);
    }
    if (!nodes.includes(target)) {
      nodes.push(target);
    }
  });
  create();

  //保证每个节点都遍历到，排除有的结点没有边的情况
  for (let i = 0; i < nodes.length; i++) {
    //该结点后边的结点都被访问过了，跳过它
    if (visited[nodes[i]] == -1) {
      continue;
    }
    DFS(i);
    if (!isDAG) {
      return {
        isDAG,
        node: nodes[i],
      };
    }
  }
  if (isDAG) {
    return {
      isDAG,
    };
  }


  //创建图,以邻接矩阵表示
  function create() {
    for (let i = 0; i < nodes.length; i++) {
      const pre = nodes[i];
      graph[pre] = {};
      for (let j = 0; j < nodes.length; j++) {
        const next = nodes[j];
        graph[pre][next] = 0;
      }
    }
    for (let k = 0; k < edges.length; k++) {
      const edge = edges[k];
      graph[edge.source][edge.target] = 1;
    }
    //初始化color数组为0，表示一开始所有顶点都未被访问过
    for (let i = 0; i < nodes.length; i++) {
      visited[nodes[i]] = 0;
    }
  }


  //图的深度遍历函数
  function DFS(i) {
    //结点i变为访问过的状态
    visited[nodes[i]] = 1;
    for (let j = 0; j < nodes.length; j++) {
      //如果当前结点有指向的结点
      if (graph[nodes[i]][nodes[j]] != 0) {
        //并且已经被访问过
        if (visited[nodes[j]] == 1) {
          isDAG = false; //有环
          break;
        } else if (visited[nodes[j]] == -1) {
          //当前结点后边的结点都被访问过，直接跳至下一个结点
          continue;
        } else {
          DFS(j); //否则递归访问
        }
      }
    }
    //遍历过所有相连的结点后，把本节点标记为-1
    visited[nodes[i]] = -1;
  }
};

function generateEdges(attrs) {
  const edges = [];
  // 遍历所有配置了监听事件的组件属性
  attrs.forEach(attr => {
    let { triggerSetting } = attr;
    let { functionBody, customFunctionParams } = triggerSetting;
    // 遍历监听事件的主函数体
    pushEdge(functionBody, attr, edges);
    // 遍历监听事件的全部自定义函数参数
    customFunctionParams.forEach(param => {
      let { functionBody } = param;
      pushEdge(functionBody, attr, edges);
    });
  });
  return edges;
}

export const pushEdge = (functionBody, attr, edges) => {
  functionBody.forEach(func => {
    // 如果是setAttr函数，则需要获取关联的组件和目标属性
    if (func.functionType === 'setAttr') {
      let { inParams } = func;
      if (inParams.length >= 3) {
        let targetPropParam = inParams[0];
        let targetAttrParam = inParams[1];
        // 目前只考虑固定值的情况
        if (targetPropParam.paramType === PARAMETER_TYPE.FIXED && targetAttrParam.paramType === PARAMETER_TYPE.FIXED) {
          let source = `${attr.prop}__${attr.attr}`;
          let target = `${targetPropParam.value}__${targetAttrParam.value}`;
          if (!edges.some(edge => {
            return edge.source === source && edge.target === target;
          })) {
            edges.push({ source, target });
          }
        }
      }
    }
  });
};
